import { lonLatToLocal } from './view-sync';

/**
 * Camera framing of the 3D viewer's drawn content. Pure: no React, no three.js.
 * A box is { min: [x, y, z], max: [x, y, z] } in the scene's local frame (x east, y up, z north, metres).
 */

/** Union of lon/lat extents { minLon, maxLon, minLat, maxLat } (nulls skipped); null when none. */
export function mergeLonLatExtents(list) {
  let out = null;
  (list || []).forEach((e) => {
    if (!e || ![e.minLon, e.maxLon, e.minLat, e.maxLat].every(Number.isFinite)) return;
    out = out
      ? { minLon: Math.min(out.minLon, e.minLon), maxLon: Math.max(out.maxLon, e.maxLon), minLat: Math.min(out.minLat, e.minLat), maxLat: Math.max(out.maxLat, e.maxLat) }
      : { ...e };
  });
  return out;
}

/** Lon/lat extent of the lon / lat arrays of a point extract ({ lon, lat, count }); null when empty. */
export function extractExtent(ext) {
  if (!ext?.count) return null;
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (let i = 0; i < ext.count; i++) {
    const lo = ext.lon[i], la = ext.lat[i];
    if (lo < minLon) minLon = lo;
    if (lo > maxLon) maxLon = lo;
    if (la < minLat) minLat = la;
    if (la > maxLat) maxLat = la;
  }
  return Number.isFinite(minLon) && Number.isFinite(minLat) ? { minLon, maxLon, minLat, maxLat } : null;
}

/**
 * Bounding box of what the scene draws: fence lines and volume cells (down to their depthRange), plus the lon/lat
 * extents of extra content (dataset point clouds, raw scatter). Falls back to `fallback` ({ min, max }, the point
 * cloud's bounds) when nothing else has a position; null when there is nothing at all.
 */
export function contentBox(models, frame, extraExtents = null, fallback = null) {
  const extents = [];
  let depth = 0;
  if (frame) {
    (models || []).forEach((m) => {
      const pts = m.line?.length === 2 ? m.line : m.cells?.[0]?.lon !== undefined ? m.cells.map((c) => [c.lon, c.lat]) : null;
      if (!pts) return;
      let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
      pts.forEach(([lo, la]) => {
        if (lo < minLon) minLon = lo;
        if (lo > maxLon) maxLon = lo;
        if (la < minLat) minLat = la;
        if (la > maxLat) maxLat = la;
      });
      extents.push({ minLon, maxLon, minLat, maxLat });
      depth = Math.max(depth, m.model?.depthRange?.[1] || 0);
    });
    (extraExtents || []).forEach((e) => extents.push(e));
  }
  const e = mergeLonLatExtents(extents);
  if (e) {
    const [x0, z0] = lonLatToLocal([e.minLon, e.minLat], frame);
    const [x1, z1] = lonLatToLocal([e.maxLon, e.maxLat], frame);
    return { min: [x0, -depth, z0], max: [x1, 0, z1] };
  }
  if (fallback?.min?.length === 3 && fallback?.max?.length === 3 && [...fallback.min, ...fallback.max].every(Number.isFinite)) {
    return { min: [...fallback.min], max: [...fallback.max] };
  }
  return null;
}

/**
 * The drawn-content box in the draped frame (TerrainMesh's group: y = (elevation - refElev) * ve). `flatBox` has y from
 * -depth to 0 in the flat frame; the ground runs from minElev to maxElev over the content, so y spans
 * (minElev - depth - refElev) * ve .. (maxElev - refElev) * ve. `plan` ({ min: [x, z], max: [x, z] }, the terrain mesh)
 * widens x / z. Returns the flat box when the terrain values are missing.
 */
export function drapedBox(flatBox, { refElev, minElev, maxElev, ve = 1, plan = null } = {}) {
  if (!flatBox) return null;
  if (![refElev, minElev, maxElev].every(Number.isFinite)) return flatBox;
  const k = Number.isFinite(ve) && ve > 0 ? Math.max(0.1, ve) : 1;
  const min = [flatBox.min[0], (minElev + flatBox.min[1] - refElev) * k, flatBox.min[2]];
  const max = [flatBox.max[0], (maxElev + flatBox.max[1] - refElev) * k, flatBox.max[2]];
  if (plan) {
    min[0] = Math.min(min[0], plan.min[0]); min[2] = Math.min(min[2], plan.min[1]);
    max[0] = Math.max(max[0], plan.max[0]); max[2] = Math.max(max[2], plan.max[1]);
  }
  return { min, max };
}

/** Whether a point is inside the box grown by half its size on every axis (at least `minMargin` metres). */
export function insideExpanded(box, p, minMargin = 50) {
  if (!box) return true;
  return [0, 1, 2].every((k) => {
    const margin = Math.max(minMargin, 0.5 * (box.max[k] - box.min[k]));
    return p[k] >= box.min[k] - margin && p[k] <= box.max[k] + margin;
  });
}

const MARGIN = 1.15;
const TOP_TILT = (10 * Math.PI) / 180; // Top leans 10 degrees off vertical (80 degrees down) over vertical content
const TILT_RATIO = 0.05; // box height / plan size above which the content counts as vertical
const MIN_SIZE = 10;

/** Distance from which a w x h rectangle (facing the camera) fits the view. */
function fitDistance(w, h, fov, aspect) {
  const t = Math.tan((fov * Math.PI) / 360);
  return Math.max(h / 2 / t, w / 2 / (t * (aspect || 1))) * MARGIN;
}

/**
 * Camera { position, target } for a preset ('top' | 'side' | 'perspective' | 'reset') framing `box`.
 * Top looks straight down over the centre and fits the plan extent; Side looks horizontally from the side
 * perpendicular to the box's long plan axis and fits its length and height; Perspective and Reset use the oblique
 * angle of the opening fit. Null for an empty box.
 * Top leans 10 degrees toward the south (north stays up) when `topTilt` is true, or by default when the box is
 * more than 5 % as tall as it is wide: vertical sections are edge-on, so they draw nothing from straight above.
 */
export function framePose(box, preset, { fov = 60, aspect = 1, topTilt } = {}) {
  if (!box) return null;
  const size = [0, 1, 2].map((k) => Math.max(MIN_SIZE, box.max[k] - box.min[k]));
  const c = [0, 1, 2].map((k) => (box.min[k] + box.max[k]) / 2);
  const [sx, sy, sz] = size;
  if (preset === 'top') {
    // the camera's screen-up is -z, so x runs along the screen width
    const d = fitDistance(sx, sz, fov, aspect) + sy / 2;
    const tilt = topTilt === undefined ? (box.max[1] - box.min[1]) > TILT_RATIO * Math.max(box.max[0] - box.min[0], box.max[2] - box.min[2], 0) : !!topTilt;
    if (tilt) return { position: [c[0], c[1] + d * Math.cos(TOP_TILT), c[2] - d * Math.sin(TOP_TILT)], target: c };
    return { position: [c[0], c[1] + d, c[2] + 0.001], target: c };
  }
  if (preset === 'side') {
    if (sx >= sz) {
      const d = fitDistance(sx, sy, fov, aspect) + sz / 2;
      return { position: [c[0], c[1], c[2] + d], target: c };
    }
    const d = fitDistance(sz, sy, fov, aspect) + sx / 2;
    return { position: [c[0] + d, c[1], c[2]], target: c };
  }
  const D = Math.max(80, Math.max(sx, sy, sz) * 1.2);
  return { position: [c[0] + D * 0.6, c[1] + D * 0.5, c[2] + D], target: c };
}

/**
 * Line segments [x0, y, z0, x1, y, z1, ...] of each fence's trace on the ground, in the scene frame from `origin`
 * { lon0, lat0 }. y is 0, or `groundY(lon, lat)` (the draped scene; the trace is cut in `steps` pieces to follow the
 * terrain). Fences without a two-point line are skipped.
 */
export function fenceTraces(fences, origin, groundY = null, steps = 24) {
  const lines = (fences || []).filter((f) => f?.line?.length === 2);
  if (!origin || !lines.length) return new Float32Array(0);
  const n = groundY ? steps : 1;
  const out = new Float32Array(lines.length * n * 6);
  let k = 0;
  const at = (lon, lat) => {
    const [x, z] = lonLatToLocal([lon, lat], origin);
    return [x, groundY ? groundY(lon, lat) : 0, z];
  };
  lines.forEach((f) => {
    const [[lo0, la0], [lo1, la1]] = f.line;
    for (let i = 0; i < n; i++) {
      const a = at(lo0 + ((lo1 - lo0) * i) / n, la0 + ((la1 - la0) * i) / n);
      const b = at(lo0 + ((lo1 - lo0) * (i + 1)) / n, la0 + ((la1 - la0) * (i + 1)) / n);
      out.set(a, k); out.set(b, k + 3); k += 6;
    }
  });
  return out;
}
